import React from "react";
import { useTranslation } from "react-i18next";

import { AIHazardShell, AIHazardStatus } from "./types";

interface SessionProgressProps {
  hazards: AIHazardShell[];
}

// Resolved = accepted | dismissed (plan §1.1). Pending hazards are never
// hidden — this persistent meter keeps the remaining review work visible
// (plan §1.2 item 5).
export const countHazardStatuses = (hazards: AIHazardShell[]) => {
  const accepted = hazards.filter(
    (hazard) => hazard.status === AIHazardStatus.accepted
  ).length;
  const dismissed = hazards.filter(
    (hazard) => hazard.status === AIHazardStatus.dismissed
  ).length;
  const total = hazards.length;
  const resolved = accepted + dismissed;

  return { accepted, dismissed, resolved, total, pending: total - resolved };
};

export const SessionProgress = ({ hazards }: SessionProgressProps) => {
  const { t } = useTranslation();

  const { accepted, dismissed, pending, resolved, total } =
    countHazardStatuses(hazards);
  const percentage = total > 0 ? Math.round((resolved / total) * 100) : 0;

  return (
    <div data-test="ai-risk-session-progress">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-medium text-gray-900">
          {t("risks.ai.board.progress", { resolved, total })}
        </p>
        <p className="text-xs text-gray-500 flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-green-500 inline-block flex-shrink-0" />
            {t("risks.ai.board.progressAccepted", { count: accepted })}
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-gray-400 inline-block flex-shrink-0" />
            {t("risks.ai.board.progressDismissed", { count: dismissed })}
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-blue-400 inline-block flex-shrink-0" />
            {t("risks.ai.board.progressPending", { count: pending })}
          </span>
        </p>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-cyan-600 h-2 rounded-full"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
